import { useQuery } from '@tanstack/react-query';
import { TenantActivityMetricsResponse } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';

export const TENANT_ACTIVITY_METRICS_QUERY_KEY = 'tenantActivityMetrics';

const LIVE_STALE_TIME_MS = 30 * 1000;
// The previous period only moves at UTC midnight, and the range in the query key moves with it.
const SETTLED_STALE_TIME_MS = 30 * 60 * 1000;

export type ActivityPeriodDays = 7 | 30 | 90;

export type ActivityMetrics = TenantActivityMetricsResponse;

export type DailyActivity = ActivityMetrics['daily'][number];

export type ActivityCountKey = Exclude<keyof DailyActivity, 'date'>;

export interface ActivityPeriods {
  current: ActivityMetrics;
  previous: ActivityMetrics;
}

const toUtcDate = (instant: Date) => instant.toISOString().slice(0, 10);

const daysBefore = (instant: Date, days: number) => {
  const shifted = new Date(instant);
  shifted.setUTCDate(shifted.getUTCDate() - days);
  return shifted;
};

export function activityPeriodRanges(days: ActivityPeriodDays, now = new Date()) {
  return {
    current: { from: toUtcDate(daysBefore(now, days - 1)), to: toUtcDate(now) },
    previous: { from: toUtcDate(daysBefore(now, 2 * days - 1)), to: toUtcDate(daysBefore(now, days)) },
  };
}

function useActivityRange(tenantId: string, range: { from: string; to: string }, live: boolean) {
  return useQuery({
    queryKey: [TENANT_ACTIVITY_METRICS_QUERY_KEY, { tenantId, ...range }],
    queryFn: async () => api.tenantActivityMetrics.getTenantActivityMetrics({ tenantId, ...range }),
    select: response => response.data,
    enabled: !!tenantId,
    staleTime: live ? LIVE_STALE_TIME_MS : SETTLED_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  });
}

export function useGetTenantActivityMetrics(days: ActivityPeriodDays) {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const ranges = activityPeriodRanges(days);
  const current = useActivityRange(tenantId, ranges.current, true);
  const previous = useActivityRange(tenantId, ranges.previous, false);

  const data: ActivityPeriods | undefined =
    current.data && previous.data ? { current: current.data, previous: previous.data } : undefined;

  return {
    data,
    isLoading: current.isLoading || previous.isLoading,
    isError: current.isError || previous.isError,
  };
}
